import { useId, useState } from 'react';
import type { ConversationFilters, DateFilter } from '../core/api/query.js';
import { SENTIMENTS } from '../core/api/types.js';
import type { QueryState, ConversationSortField } from './useQueryState.js';

/**
 * The conversation query surface.
 *
 * Phase 2 shipped 11 filters, 4 sort fields and keyset paging, all verified working, and until
 * now none of it was reachable. Two design constraints from the build order shape this:
 *
 *  1. **The three date modes are mutually exclusive** and sending two is a 400. So mode is a
 *     single radio group and only that mode's inputs exist — picking two is unrepresentable,
 *     not merely rejected.
 *  2. **Filters and sort are separate controls**, deliberately. Changing sort invalidates the
 *     keyset cursor and restarts paging; changing a filter restarts the query anyway. Putting
 *     both in one panel invites someone to change them together and makes the reset look like
 *     a bug.
 *
 * Named presets rather than raw times for the recurring window: "business hours" and
 * "overnight" are what people mean. Nobody should have to reason about 22:00–02:00 wrapping
 * past midnight to express "overnight", even though that is exactly what the API wants.
 */
export function Filters({ state, agents, showAssignment }: {
  state: QueryState;
  agents?: readonly { id: string; name: string }[];
  showAssignment?: boolean;
}) {
  const { filters, activeCount, setFilters, clear } = state;
  const [open, setOpen] = useState(false);
  const panelId = useId();

  const dateMode = filters.date?.mode ?? 'any';
  const windowPreset = presetOf(filters);

  return (
    <section className="filters" aria-label="Filter calls">
      <div className="filters-bar">
        <button
          className="btn btn-small"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => setOpen((o) => !o)}
        >
          Filters{activeCount > 0 ? ` (${activeCount})` : ''}
        </button>
        {activeCount > 0 && (
          <button className="linkbtn" onClick={clear}>Clear all</button>
        )}
      </div>

      <div id={panelId} className="filters-panel" hidden={!open}>
        <Field label="Sentiment">
          <select className="input" value={filters.sentiment ?? ''}
            onChange={(e) => setFilters({ ...filters, sentiment: e.target.value === '' ? undefined : e.target.value as never })}>
            <option value="">Any</option>
            {SENTIMENTS.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </Field>

        <Field label="Outcome" hint="Matched as text — outcomes come from the provider, so there is no fixed list.">
          <input className="input" type="text" value={filters.outcome ?? ''}
            placeholder="e.g. Booking confirmed"
            onChange={(e) => setFilters({ ...filters, outcome: e.target.value || undefined })} />
        </Field>

        {agents && agents.length > 0 && (
          <Field label="Agent">
            <select className="input" value={filters.agentId ?? ''}
              onChange={(e) => setFilters({ ...filters, agentId: e.target.value || undefined })}>
              <option value="">Any</option>
              {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </Field>
        )}

        {showAssignment && (
          <Field label="Assignment">
            <select className="input"
              value={filters.unassignedOnly ? 'unassigned' : ''}
              onChange={(e) => setFilters({
                ...filters,
                unassignedOnly: e.target.value === 'unassigned' ? true : undefined,
              })}>
              <option value="">Any</option>
              <option value="unassigned">Not assigned to anyone</option>
            </select>
          </Field>
        )}

        <fieldset className="fieldset">
          <legend className="field-label">When</legend>
          {/* One radio group, so two date modes cannot both be set. The API returns 400 for
              that combination; making it unreachable beats explaining it afterwards. */}
          <Radio name="datemode" value="any" checked={dateMode === 'any'} label="Any time"
            onSelect={() => setFilters({ ...filters, date: undefined })} />
          <Radio name="datemode" value="day" checked={dateMode === 'day'} label="A single day"
            onSelect={() => setFilters({ ...filters, date: { mode: 'day', date: today() } })} />
          <Radio name="datemode" value="range" checked={dateMode === 'dayRange'} label="A range of days"
            onSelect={() => setFilters({ ...filters, date: { mode: 'dayRange', fromDate: weekAgo(), toDate: today() } })} />

          {filters.date?.mode === 'day' && (
            <input className="input" type="date" value={filters.date.date}
              aria-label="Day" onChange={(e) => setDate(setFilters, filters, { mode: 'day', date: e.target.value })} />
          )}
          {filters.date?.mode === 'dayRange' && (
            <DayRange range={filters.date} filters={filters} setFilters={setFilters} />
          )}
          <p className="hint">Days are worked out in your agency’s time zone.</p>
        </fieldset>

        <Field label="Time of day">
          <select className="input" value={windowPreset}
            onChange={(e) => setFilters({ ...filters, timeWindow: WINDOWS[e.target.value] })}>
            <option value="any">Any time of day</option>
            <option value="business">Business hours (9am–5pm)</option>
            <option value="evening">Evenings (5pm–10pm)</option>
            <option value="overnight">Overnight (10pm–2am)</option>
          </select>
        </Field>
      </div>
    </section>
  );
}

/**
 * Sort, kept apart from the filters on purpose. Changing it throws away the keyset cursor and
 * restarts paging from the first page.
 */
export function SortControl({ state }: { state: QueryState }) {
  const { sort, setSort } = state;
  return (
    <div className="sortbar">
      <label className="field-label" htmlFor="sortby">Sort by</label>
      <select id="sortby" className="input input-inline" value={sort.sortBy}
        onChange={(e) => setSort({ ...sort, sortBy: e.target.value as ConversationSortField })}>
        <option value="StartedAt">When it happened</option>
        <option value="Duration">How long it was</option>
        <option value="Sentiment">Sentiment</option>
        <option value="AssignedAt">When it was assigned</option>
      </select>
      <button className="btn btn-small"
        onClick={() => setSort({ ...sort, sortDirection: sort.sortDirection === 'Desc' ? 'Asc' : 'Desc' })}
        aria-label={`Sorted ${sort.sortDirection === 'Desc' ? 'newest first' : 'oldest first'}. Reverse the order.`}>
        {sort.sortDirection === 'Desc' ? 'Newest first' : 'Oldest first'}
      </button>
    </div>
  );
}

const WINDOWS: Record<string, { fromTime: string; toTime: string } | undefined> = {
  any: undefined,
  business: { fromTime: '09:00', toTime: '17:00' },
  evening: { fromTime: '17:00', toTime: '22:00' },
  // End before start wraps past midnight - which is exactly what "overnight" means, and
  // exactly what nobody should have to work out for themselves.
  overnight: { fromTime: '22:00', toTime: '02:00' },
};

function presetOf(f: ConversationFilters): string {
  if (!f.timeWindow) return 'any';
  for (const [k, v] of Object.entries(WINDOWS)) {
    if (v && v.fromTime === f.timeWindow.fromTime && v.toTime === f.timeWindow.toTime) return k;
  }
  return 'any';
}

function setDate(set: (f: ConversationFilters) => void, f: ConversationFilters, date: DateFilter) {
  set({ ...f, date });
}

/**
 * Its own component so the narrowed range type survives into the change handlers - a spread of
 * the union inside a callback loses the narrowing and needs a cast, and a cast here would be
 * hiding the one thing this control exists to get right.
 *
 * `min`/`max` keep the two ends from crossing, so the backwards range the API rejects with a
 * 400 cannot be expressed in the first place.
 */
function DayRange({ range, filters, setFilters }: {
  range: Extract<DateFilter, { mode: 'dayRange' }>;
  filters: ConversationFilters;
  setFilters: (f: ConversationFilters) => void;
}) {
  return (
    <div className="row-inputs">
      <input className="input" type="date" aria-label="From" value={range.fromDate} max={range.toDate}
        onChange={(e) => setFilters({ ...filters, date: { ...range, fromDate: e.target.value } })} />
      <input className="input" type="date" aria-label="To" value={range.toDate} min={range.fromDate}
        onChange={(e) => setFilters({ ...filters, date: { ...range, toDate: e.target.value } })} />
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  const id = useId();
  return (
    <div className="field">
      <label className="field-label" htmlFor={id}>{label}</label>
      <div id={id}>{children}</div>
      {hint && <p className="hint">{hint}</p>}
    </div>
  );
}

function Radio({ name, value, checked, label, onSelect }: {
  name: string; value: string; checked: boolean; label: string; onSelect: () => void;
}) {
  return (
    <label className="radio">
      <input type="radio" name={name} value={value} checked={checked} onChange={onSelect} />
      <span>{label}</span>
    </label>
  );
}

const today = () => new Date().toISOString().slice(0, 10);
const weekAgo = () => new Date(Date.now() - 6 * 864e5).toISOString().slice(0, 10);
